import apiClient from './client';

export const WORKSPACE_SCHEMA_VERSION = 1 as const;

export interface WorkspaceCardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WorkspaceCardLayouts {
  lg: WorkspaceCardLayoutItem[];
  md: WorkspaceCardLayoutItem[];
  sm: WorkspaceCardLayoutItem[];
}

export interface WorkspaceLayoutPreference {
  schemaVersion: typeof WORKSPACE_SCHEMA_VERSION;
  orderedCardIds: string[];
  /** Ordered KPI selection; absent on layouts saved before KPI customization. */
  orderedKpiIds?: string[];
  pinnedCardIds: string[];
  cardLayouts?: WorkspaceCardLayouts;
  updatedAt: string;
}

export interface WorkspaceLayoutInput {
  schemaVersion: typeof WORKSPACE_SCHEMA_VERSION;
  orderedCardIds: string[];
  /** Ordered KPI selection; absent on layouts saved before KPI customization. */
  orderedKpiIds?: string[];
  pinnedCardIds: string[];
  cardLayouts: WorkspaceCardLayouts;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export async function getWorkspacePreferences(): Promise<WorkspaceLayoutPreference | null> {
  const response = await apiClient.get<ApiEnvelope<{ layout: WorkspaceLayoutPreference | null }>>(
    '/api/workspace/preferences'
  );
  return response.data.data.layout;
}

export async function putWorkspacePreferences(
  layout: WorkspaceLayoutInput
): Promise<WorkspaceLayoutPreference> {
  const response = await apiClient.put<ApiEnvelope<{ layout: WorkspaceLayoutPreference }>>(
    '/api/workspace/preferences',
    { layout }
  );
  return response.data.data.layout;
}

export interface WorkspaceCardContent {
  state: 'unknown' | 'loading' | 'error' | 'empty' | 'populated';
  itemCount?: number;
  metricCount?: number;
  columnCount?: number;
  seriesCount?: number;
  categoryCount?: number;
  template?: string;
  calendarView?: 'day' | 'week' | 'month';
  calendarRows?: number;
  attention?: 'normal' | 'high';
}
export type ReportWorkspaceCardContent = (id: string, content: WorkspaceCardContent) => void;
export interface WorkspaceCardContext {
  id: string;
  title: string;
  module: string;
  kind: 'card' | 'chart' | 'calendar' | 'insights';
  empty?: boolean;
  content?: WorkspaceCardContent;
}
export interface WorkspaceOptimization {
  layout: WorkspaceLayoutInput;
  explanation: string;
}
export async function optimizeWorkspaceLayout(layout: WorkspaceLayoutInput, cards: WorkspaceCardContext[]): Promise<WorkspaceOptimization> {
  const response = await apiClient.post<ApiEnvelope<WorkspaceOptimization>>('/api/workspace/optimize', { layout, cards }, { timeout: 50_000 });
  return response.data.data;
}
